/// <reference path="./filters.d.ts" />

import staticStore from '@aytm/app/insights/dashboard/stores/static.store'

import {
  VIEWER_FILTERS_MASK,
  EDITOR_FILTERS_MASK,
  SHARED_FILTERS_MASK
} from '@aytm/app/insights/dashboard/lib/constants'

import { List } from 'immutable'
import { optionsSortComparerMaker } from './strings'

export const convertFilterOptions = (options = []) => {
  if (!options) return null
  return options.reduce((optionsByCategories, filterOptions) => {
    const [filterCategory] = filterOptions.id.split(':', 2)
    if (null == optionsByCategories[filterCategory]) {
      optionsByCategories[filterCategory] = []
    }
    optionsByCategories[filterCategory].push(filterOptions)
    return optionsByCategories
  }, {})
}

export const updateFilterTags = (options, tags) => {
  const newOptions = Object.assign({}, options || {})
  newOptions.variable = (newOptions.variable || []).slice()
  const index = newOptions.variable.findIndex(v => 'variable:Tag' === v.id)
  if (index >= 0) {
    const newTagOptions = newOptions.variable[index].options.slice()
    for (const tag of tags) {
      if (!newTagOptions.some(v => tag === v.value)) newTagOptions.push({ value: tag, label: tag })
    }
    newOptions.variable[index] = { ...newOptions.variable[index], options: newTagOptions }
  } else {
    const newTagOptions = tags.map(v => {
      return { value: v, label: v }
    })
    newOptions.variable.push({
      id: 'variable:Tag',
      title: 'Tag',
      options: newTagOptions
    })
  }
  return newOptions
}

export const sortOptions = (options, ascending) => (options || []).sort(optionsSortComparerMaker(ascending))

export const addFiltersValue = (filters, id, value) => {
  const newValues = filters.getIn([id, 'values'], List()).push(value)
  return filters.setIn([id, 'values'], newValues)
}

export const deleteFiltersValue = (filters, id, value) => {
  const deleteIndex = filters.getIn([id, 'values'], List()).indexOf(value)
  if (deleteIndex >= 0) {
    const newValues = filters.getIn([id, 'values'], List()).delete(deleteIndex)
    return newValues.size ? filters.setIn([id, 'values'], newValues) : filters.delete(id)
  } else return filters
}

export const filtersHasValue = (filters, id, value) => filters.getIn([id, 'values'], List()).includes(value)

export const dataHasValue = (data, key, value) =>
  Object.values(data || {}).some(v => {
    const val = v?.attributes?.[key]?.value || []
    return Array.isArray(val) ? val.includes(value) : val == value
  })

/**
 * @typedef {import('@aytm/app/insights/dashboard/components/hooks').SOURCE_FILTERS_MASK} SOURCE_FILTERS_MASK
 * @typedef {import('@aytm/app/insights/dashboard/components/hooks').MODEL_FILTERS_MASK} MODEL_FILTERS_MASK
 */

/**
 * @template {T}
 * @param {({ toJS(): T })|T} filters
 * @param {SOURCE_FILTERS_MASK|MODEL_FILTERS_MASK} fields
 * @returns {{[k: string]:unknown}}
 */
export const readFilters = (filters, fields) => {
  const jsFilters = filters ? (filters.toJS ? filters.toJS() : filters) : []
  return (Array.isArray(jsFilters) ? jsFilters : Object.values(jsFilters))
    .filter(v => fields.includes(v?.uid?.split(':')[0]))
    .reduce((acc, cur) => Object.assign(acc, { [cur.uid]: cur.values }), {})
}

export const selectFilters = (filters, mask) => {
  let result = filters
  if (result && mask) {
    for (const key of filters.keys()) {
      if (!mask.includes(key.split(':')[0])) result = result.delete(key)
    }
  }
  return result
}

export const replaceFilters = (oldFilters, newFilters, mask) => {
  if (!oldFilters) {
    return selectFilters(newFilters, mask)
  }
  let result = oldFilters

  const processedKeys = new window.Set()
  // Update/Delete filters
  for (const key of result.keys()) {
    if (!mask || (mask && mask.includes(key.split(':')[0]))) {
      processedKeys.add(key)
      result = newFilters.has(key) ? result.set(key, newFilters.get(key)) : result.delete(key)
    }
  }
  // Add new filters
  for (const key of newFilters.keys()) {
    if (processedKeys.has(key)) {
      continue
    }
    if (!mask || (mask && mask.includes(key.split(':')[0]))) {
      result = result.has(key) ? result : result.set(key, newFilters.get(key))
    }
  }
  return result
}

export const findOption = (options, value) => {
  for (const option of options) {
    if (option.value === value) return option
    else if (option.options) {
      const found = findOption(option.options, value)
      if (found) return found
    }
  }
  return null
}

export const groupOptions = options => {
  let currentOptions = options
  return options.reduce((acc, cur) => {
    if (cur.group) {
      const newOptions = []
      acc.push({ ...cur, options: newOptions })
      currentOptions = newOptions
    } else {
      currentOptions.push(cur)
    }
    return acc
  }, [])
}

export const mergeFilters = (filters, patch) => {
  let result = filters
  if (filters && patch) {
    for (const [uid, patchValues] of Object.entries(patch)) {
      let values = result.getIn([uid, 'values'], List())
      for (const val of patchValues) {
        if (!values.includes(val)) {
          values = values.push(val)
        }
      }
      result = result.setIn([uid, 'values'], values)
    }
  }
  return result
}

export const deleteFilters = (filters, skipUids) => {
  let result = filters
  if (filters) {
    if (skipUids && skipUids.length) {
      for (const uid of filters.keys()) {
        if (!skipUids.includes(uid)) {
          result = result.delete(uid)
        }
      }
      return result
    } else {
      return result.clear()
    }
  }
  return result
}

/**
 *
 */
export const getAllFiltersMask = (state, blockUID) => {
  const currentSchema = state.currentSchema
  if (currentSchema) {
    const currentBlockUID = blockUID || currentSchema.get('currentBlockUID')
    const currentBlock = currentSchema.getIn(['blocks', currentBlockUID])
    if (currentBlock) {
      const roles = state.accessKey?.roles || []
      const isEditorMode = roles.includes('editor')
      const isSharedView = staticStore.isSharedView || false
      return isSharedView ? SHARED_FILTERS_MASK : isEditorMode ? EDITOR_FILTERS_MASK : VIEWER_FILTERS_MASK
    }
  }
  return []
}
